<template>
  <div>
    <!-- Loading skeleton -->
    <div v-if="loading">
      <b-card>
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </b-card>
    </div>

    <template v-else>
      <!-- FCR Summary Card -->
      <b-row>
        <b-col md="4">
          <b-card class="text-center">
            <h6 class="text-muted">{{ $t('features.fcr.score') }}</h6>
            <h1 :class="fcrColorClass" class="display-4 font-weight-bold">
              {{ fcrData.fcr || '—' }}
            </h1>
            <p class="text-muted mb-0">{{ $t('features.fcr.benchmark') }}: <strong>{{ fcrData.industry_benchmark || 1.75 }}</strong></p>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center">
            <h6 class="text-muted">Total Feed (kg)</h6>
            <h2 class="text-primary">{{ fcrData.total_feed_kg || '—' }}</h2>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="text-center">
            <h6 class="text-muted">Est. Weight (kg)</h6>
            <h2 class="text-info">{{ fcrData.estimated_weight_kg || '—' }}</h2>
            <p class="text-muted mb-0">Flock Age: {{ fcrData.flock_age_days || '—' }} days</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- FCR Status Alert -->
      <b-card>
        <b-row class="align-items-center">
          <b-col>
            <span class="font-weight-bold">FCR Status: </span>
            <b-badge :variant="fcrBadgeVariant" class="ml-1">{{ fcrStatusLabel }}</b-badge>
          </b-col>
          <b-col class="text-right">
            <b-button variant="outline-primary" size="sm" @click="fetchData">
              Refresh
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <!-- FCR Trend Chart -->
      <b-card title="FCR Trend">
        <div v-if="!chartData.series[0].data.length" class="text-center py-4 text-muted">
          No FCR history data available to render chart.
        </div>
        <vue-apex-charts
          v-else
          type="line"
          height="300"
          :options="chartOptions"
          :series="chartData.series"
        />
      </b-card>
    </template>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import VueApexCharts from 'vue-apexcharts'
import { BBadge, BSpinner, BCard, BRow, BCol, BButton } from 'bootstrap-vue'

export default {
  name: 'FlockFcr',
  components: { VueApexCharts, BBadge, BSpinner, BCard, BRow, BCol, BButton },
  data() {
    return {
      loading: false,
      fcrData: {},
      rows: [],
      chartData: {
        series: [
          { name: 'Your FCR', data: [] },
          { name: 'Industry Benchmark', data: [] },
        ],
      },
      chartOptions: {
        chart: { type: 'line', toolbar: { show: false } },
        stroke: { curve: 'smooth', width: [3, 2] },
        colors: ['#7367F0', '#EA5455'],
        xaxis: { categories: [], title: { text: 'Day of Age' } },
        yaxis: { title: { text: 'FCR' }, min: 0, max: 3 },
        markers: { size: 4 },
        legend: { position: 'top' },
        annotations: {
          yaxis: [
            {
              y: 1.75,
              borderColor: '#28C76F',
              label: { text: 'Target 1.75', style: { color: '#28C76F' } },
            },
          ],
        },
      },
    }
  },
  computed: {
    fcrValue() {
      return parseFloat(this.fcrData.fcr) || null
    },
    fcrColorClass() {
      if (!this.fcrValue) return 'text-muted'
      if (this.fcrValue < 1.75) return 'text-success'
      if (this.fcrValue <= 2.0) return 'text-warning'
      return 'text-danger'
    },
    fcrBadgeVariant() {
      if (!this.fcrValue) return 'secondary'
      if (this.fcrValue < 1.75) return 'success'
      if (this.fcrValue <= 2.0) return 'warning'
      return 'danger'
    },
    fcrStatusLabel() {
      if (!this.fcrValue) return 'No Data'
      if (this.fcrValue < 1.75) return this.$t('features.fcr.good')
      if (this.fcrValue <= 2.0) return this.$t('features.fcr.average')
      return this.$t('features.fcr.poor')
    },
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      const { farmId, shedId, flockId } = this.$route.params
      this.loading = true
      try {
        const { data } = await axios.get(`web/farms/${farmId}/sheds/${shedId}/flocks/${flockId}/fcr`)
        this.fcrData = data
        this.rows = data.history || []
        // Build chart data from history
        const history = data.history || []
        const benchmark = parseFloat(data.industry_benchmark) || 1.75
        const days = history.map(h => h.flock_age_day || h.day || h.date)
        const yourFcr = history.map(h => parseFloat(h.fcr) || null)
        const benchmarkLine = history.map(() => benchmark)
        this.chartOptions = {
          ...this.chartOptions,
          xaxis: { ...this.chartOptions.xaxis, categories: days },
        }
        this.chartData = {
          series: [
            { name: 'Your FCR', data: yourFcr },
            { name: 'Industry Benchmark', data: benchmarkLine },
          ],
        }
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load FCR data', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    getFcrClass(val) {
      const v = parseFloat(val)
      if (v < 1.75) return 'text-success font-weight-bold'
      if (v <= 2.0) return 'text-warning font-weight-bold'
      return 'text-danger font-weight-bold'
    },
  },
}
</script>

<style scoped>
.display-4 {
  font-size: 2.5rem;
}
</style>
